import React, { useEffect, useState } from "react";
import { Trophy, Trash, Plus, X, PencilSimple, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const SPORTS = [
  "football", "basketball", "cricket", "tennis", "hockey", "rugby",
  "badminton", "volleyball", "boxing", "kabaddi", "swimming", "athletics", "general",
];

const emptyForm = {
  title: "", sport: "football", match_date: "", opponent_id: "",
  formation: "", starting_lineup: [], substitutes: [],
  tactical_plan: "", set_pieces: "", key_matchups: "",
};

function LineupList({ label, items, onChange, testid }) {
  const add = () => onChange([...items, { position: "", name: "", notes: "" }]);
  const update = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="border border-slate-200 bg-slate-50 p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny text-slate-600">{label} ({items.length})</div>
        <button onClick={add} type="button" className="text-cyan-600 hover:text-cyan-700 text-xs flex items-center gap-1"><Plus size={12} /> ADD</button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No entries yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 items-center">
              <input placeholder="Pos" className="col-span-2 border border-slate-300 px-2 py-1 text-xs bg-white" value={row.position} onChange={(e) => update(i, "position", e.target.value)} />
              <input placeholder="Name" className="col-span-6 border border-slate-300 px-2 py-1 text-xs bg-white" value={row.name} onChange={(e) => update(i, "name", e.target.value)} />
              <input placeholder="Notes" className="col-span-3 border border-slate-300 px-2 py-1 text-xs bg-white" value={row.notes} onChange={(e) => update(i, "notes", e.target.value)} />
              <button type="button" onClick={() => remove(i)} className="col-span-1 text-red-500 hover:text-red-700"><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SportsMatchDay() {
  const [rows, setRows] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/sports-coach/matchday"),
      api.get("/sports-coach/opponents"),
    ]).then(([m, o]) => {
      setRows(m.data.items || []);
      setOpponents(o.data.items || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row.matchday_id);
    setForm({
      title: row.title || "",
      sport: row.sport || "football",
      match_date: row.match_date || "",
      opponent_id: row.opponent_id || "",
      formation: row.formation || "",
      starting_lineup: row.starting_lineup || [],
      substitutes: row.substitutes || [],
      tactical_plan: row.tactical_plan || "",
      set_pieces: row.set_pieces || "",
      key_matchups: row.key_matchups || "",
    });
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.match_date) { toast.error("Match date required"); return; }
    setSaving(true);
    try {
      const body = { ...form, title: form.title.trim(), opponent_id: form.opponent_id || null };
      if (editing) {
        await api.patch(`/sports-coach/matchday/${editing}`, body);
        toast.success("Updated");
      } else {
        await api.post("/sports-coach/matchday", body);
        toast.success("Match-day plan saved");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this playbook?")) return;
    await api.delete(`/sports-coach/matchday/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="sc-matchday">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <div className="label-tiny text-slate-500">// MATCH-DAY PLAYBOOK</div>
          <div className="text-sm text-slate-600">Starting XI / roster · formation · tactical plan · set pieces.</div>
        </div>
        <button onClick={showForm ? cancel : openNew} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-matchday-new-btn">
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "CANCEL" : "NEW PLAYBOOK"}
        </button>
      </div>

      {showForm && (
        <div className="border border-slate-200 bg-white p-4 mb-6 space-y-3" data-testid="sc-matchday-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Playbook title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sc-md-title" />
            <input type="date" className="border border-slate-300 px-3 py-2 text-sm bg-white" value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })} data-testid="sc-md-date" />
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white">
              {SPORTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}
            </select>
            <select value={form.opponent_id} onChange={(e) => setForm({ ...form, opponent_id: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-md-opponent">
              <option value="">— No opponent linked —</option>
              {opponents.map((o) => <option key={o.opponent_id} value={o.opponent_id}>{o.name}</option>)}
            </select>
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white md:col-span-2" placeholder="Formation (e.g. 4-3-3, 2-3 zone)" value={form.formation} onChange={(e) => setForm({ ...form, formation: e.target.value })} data-testid="sc-md-formation" />
          </div>

          <LineupList label="STARTING LINEUP" items={form.starting_lineup} onChange={(v) => setForm({ ...form, starting_lineup: v })} testid="sc-md-lineup" />
          <LineupList label="SUBSTITUTES" items={form.substitutes} onChange={(v) => setForm({ ...form, substitutes: v })} testid="sc-md-subs" />

          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={3} placeholder="Tactical plan" value={form.tactical_plan} onChange={(e) => setForm({ ...form, tactical_plan: e.target.value })} data-testid="sc-md-tactical" />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={2} placeholder="Set pieces / special plays" value={form.set_pieces} onChange={(e) => setForm({ ...form, set_pieces: e.target.value })} />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={2} placeholder="Key matchups (their #10 vs our #6…)" value={form.key_matchups} onChange={(e) => setForm({ ...form, key_matchups: e.target.value })} />
          <div className="flex justify-end">
            <button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-md-submit">
              {saving ? "SAVING…" : editing ? "UPDATE" : "SAVE"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="sc-matchday-empty">
          <Trophy size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No match-day playbooks yet.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((m) => {
            const isUpcoming = m.match_date && new Date(m.match_date) >= new Date(new Date().toDateString());
            return (
              <div key={m.matchday_id} className="border border-slate-200 bg-white p-4" data-testid={`sc-matchday-card-${m.matchday_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-heading text-lg">{m.title}</div>
                      {isUpcoming && <span className="text-[9px] bg-cyan-100 text-cyan-800 border border-cyan-300 px-1.5 py-0.5">UPCOMING</span>}
                    </div>
                    <div className="text-[10px] tracking-widest text-slate-500 mt-1">
                      {(m.sport || "").replace(/_/g, " ").toUpperCase()} · {m.match_date && new Date(m.match_date).toDateString()}
                      {m.opponent_name && <> · VS {m.opponent_name.toUpperCase()}</>}
                      {m.formation && <> · {m.formation}</>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setDetail(m)} className="text-slate-500 hover:text-cyan-700 text-xs label-tiny" data-testid={`sc-matchday-view-${m.matchday_id}`}>VIEW</button>
                    <button onClick={() => openEdit(m)} className="text-slate-500 hover:text-cyan-700" data-testid={`sc-matchday-edit-${m.matchday_id}`}><PencilSimple size={16} /></button>
                    <button onClick={() => remove(m.matchday_id)} className="text-slate-500 hover:text-red-600" data-testid={`sc-matchday-delete-${m.matchday_id}`}><Trash size={16} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-2">
                  <div className="flex items-center gap-1.5"><Users size={12} className="text-cyan-600" /> {m.starting_lineup?.length || 0} starters</div>
                  <div className="flex items-center gap-1.5 text-slate-500">{m.substitutes?.length || 0} subs</div>
                  <div className={`col-span-2 truncate text-slate-600 ${m.tactical_plan ? "" : "italic text-slate-400"}`}>{m.tactical_plan || "No tactical plan yet"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)} data-testid="sc-matchday-detail">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="font-heading text-xl">{detail.title}</div>
                <div className="text-[10px] tracking-widest text-cyan-700 mt-1">
                  {(detail.sport || "").replace(/_/g, " ").toUpperCase()} · {detail.match_date && new Date(detail.match_date).toDateString()}
                  {detail.opponent_name && <> · VS {detail.opponent_name.toUpperCase()}</>}
                  {detail.formation && <> · {detail.formation}</>}
                </div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="sc-matchday-detail-close"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4 text-sm">
              {detail.starting_lineup?.length > 0 && (
                <div>
                  <div className="label-tiny text-emerald-700 mb-2">STARTING LINEUP</div>
                  <div className="border border-slate-200">
                    {detail.starting_lineup.map((p, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 px-2 py-1 border-b border-slate-100 last:border-0 text-xs">
                        <div className="col-span-2 font-heading text-cyan-700">{p.position}</div>
                        <div className="col-span-4">{p.name}</div>
                        <div className="col-span-6 text-slate-500">{p.notes}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.substitutes?.length > 0 && (
                <div>
                  <div className="label-tiny text-slate-500 mb-2">SUBSTITUTES</div>
                  <div className="border border-slate-200">
                    {detail.substitutes.map((p, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 px-2 py-1 border-b border-slate-100 last:border-0 text-xs">
                        <div className="col-span-2 font-heading text-slate-600">{p.position}</div>
                        <div className="col-span-4">{p.name}</div>
                        <div className="col-span-6 text-slate-500">{p.notes}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.tactical_plan && (<div><div className="label-tiny text-cyan-700 mb-1">TACTICAL PLAN</div><div className="whitespace-pre-wrap text-slate-700">{detail.tactical_plan}</div></div>)}
              {detail.set_pieces && (<div><div className="label-tiny text-cyan-700 mb-1">SET PIECES</div><div className="whitespace-pre-wrap text-slate-700">{detail.set_pieces}</div></div>)}
              {detail.key_matchups && (<div><div className="label-tiny text-cyan-700 mb-1">KEY MATCHUPS</div><div className="whitespace-pre-wrap text-slate-700">{detail.key_matchups}</div></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
